<script setup lang="ts">
import { callOnce, computed, ref, useSpotMedia, watch } from "#imports";
import { onKeyUp } from "@vueuse/core";

const props = defineProps<{
  mediaIds: string[];
  index: number;
}>();

const spotMediaStore = useSpotMedia();
const media = computed(() => spotMediaStore.getByIds(props.mediaIds).value!);

const loadData = async () => {
  await spotMediaStore.fetchByIds(props.mediaIds);
};

await callOnce(`MPMedia-${props.mediaIds.join(",")}`, () => loadData, {
  mode: "navigation",
});
watch(() => props.mediaIds, loadData);

const currentIndex = ref<number>(props.index);
const currentMedia = computed(() => media.value[currentIndex.value]!);

const nextMedia = () => {
  if (currentIndex.value < media.value.length - 1) {
    currentIndex.value++;
  } else {
    currentIndex.value = 0;
  }
};

const prevMedia = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
  } else {
    currentIndex.value = media.value.length - 1;
  }
};

onKeyUp("ArrowRight", nextMedia);
onKeyUp("ArrowLeft", prevMedia);
</script>
<template>
  <div class="media-container" v-if="currentMedia">
    <button class="nav-button nav-button-prev" @click="prevMedia">
      <i class="ri-arrow-left-s-line" />
    </button>
    <img v-if="currentMedia.type === 'image'" :src="currentMedia.url" />
    <video
      controls
      disablePictureInPicture
      v-else-if="currentMedia.type === 'video'"
      :src="currentMedia.url"
    />
    <button class="nav-button nav-button-next" @click="nextMedia">
      <i class="ri-arrow-right-s-line" />
    </button>
  </div>
</template>
<style lang="postcss" scoped>
.media-container {
  max-width: 90dvw;
  max-height: 90dvh;
  position: relative;

  img {
    height: 100%;
    max-height: 750px;
  }
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  font-size: 1.5rem;
  color: var(--color-slate-500);
  background-color: var(--color-slate-50);
  border: 2px solid var(--color-slate-100);
  opacity: 0.8;
  border-radius: 50%;
  width: 2.35rem;
  height: 2.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: auto;
  box-shadow: var(--shadow-base);
}

.nav-button:hover {
  background-color: var(--color-slate-100);
  border: 2px solid var(--color-slate-200);
}

.nav-button-prev {
  left: 0.5rem;
}

.nav-button-next {
  right: 0.5rem;
}
</style>
